import { push } from 'react-router-redux';

import type { IUser } from '../../domain';
import { endpoint } from '../../helpers/Endpoints';
import { route } from '../../helpers/Routes';
import type { IToastMessage } from '../../helpers/Toastr';
import { toastr } from '../../helpers/Toastr';
import * as FetchAction from '../FetchAction';
import * as FetchActionGeneric from '../FetchActionGeneric';
import type { DispatchFunc } from '../types';

export interface INewUserRequest {
	username: string;
	firstName: string;
	lastName: string;
	password: string;
	roles: string[];
	userProfileId: number;
}

export const LOADING_USERS = '@admin-users:LOADING_USERS';
export const LOADING_USERS_SUCCESS = '@admin-users:LOADING_USERS_SUCCESS';
export const LOADING_USERS_ERROR = '@admin-users:LOADING_USERS_ERROR';

export function loadUsers(
	pageNumber: number,
	itemsPerPage: number,
	searchTerm: string,
	customerId: number,
	showUnassignedToCustomer: boolean
) {
	return FetchAction.CreateFetchAction({
		url: endpoint.users({
			pi: pageNumber,
			ps: itemsPerPage,
			st: searchTerm,
			customerId: customerId <= 0 ? undefined : customerId,
			showUnassignedToCustomer,
		}),
		startAction: LOADING_USERS,
		startActionPayload: {
			pageNumber: pageNumber || 1,
			itemsPerPage: itemsPerPage || 20,
			searchTerm: searchTerm || '',
			selectedCustomerId: customerId <= 0 ? undefined : customerId,
			showUnassignedToCustomer,
		},
	});
}

export const LOADING_USER = '@admin-users:LOADING_USER';
export const LOADING_USER_SUCCESS = '@admin-users:LOADING_USER_SUCCESS';
export const LOADING_USER_ERROR = '@admin-users:LOADING_USER_ERROR';

export function loadUser(userId: number) {
	return FetchAction.CreateFetchAction({
		url: endpoint.user(userId),
		startAction: LOADING_USER,
	});
}

export const LOADING_USER_PROFILES = '@admin-users:LOADING_USER_PROFILES';
export const LOADING_USER_PROFILES_SUCCESS =
	'@admin-users:LOADING_USER_PROFILES_SUCCESS';
export const LOADING_USER_PROFILES_ERROR =
	'@admin-users:LOADING_USER_PROFILES_ERROR';

export function loadUserProfiles() {
	return FetchAction.CreateFetchAction({
		url: endpoint.userProfiles(),
		startAction: LOADING_USER_PROFILES,
	});
}

export const LOADING_CUSTOMERS = '@admin-users:LOADING_CUSTOMERS';
export const LOADING_CUSTOMERS_SUCCESS =
	'@admin-users:LOADING_CUSTOMERS_SUCCESS';
export const LOADING_CUSTOMERS_ERROR = '@admin-users:LOADING_CUSTOMERS_ERROR';

export function loadCustomers(
	userId: number,
	pageNumber: number,
	filter: string
) {
	return FetchAction.CreateFetchAction({
		url: endpoint.userCustomerAssignment(userId, {
			pi: pageNumber,
			ps: 10,
			filter,
		}),
		startAction: LOADING_CUSTOMERS,
		startActionPayload: {
			pageNumber: pageNumber || 1,
		},
	});
}

export const LOADING_ALL_CUSTOMERS = '@admin-users:LOADING_ALL_CUSTOMERS';
export const LOADING_ALL_CUSTOMERS_SUCCESS =
	'@admin-users:LOADING_ALL_CUSTOMERS_SUCCESS';
export const LOADING_ALL_CUSTOMERS_ERROR =
	'@admin-users:LOADING_ALL_CUSTOMERS_ERROR';

export function loadAllCustomers() {
	return FetchAction.CreateFetchAction({
		url: endpoint.customers(),
		startAction: LOADING_ALL_CUSTOMERS,
	});
}

export const SAVING_USER_CUSTOMERS = '@admin-users:SAVING_USER_CUSTOMERS';
export const SAVING_USER_CUSTOMERS_SUCCESS =
	'@admin-users:SAVING_USER_CUSTOMERS_SUCCESS';
export const SAVING_USER_CUSTOMERS_ERROR =
	'@admin-users:SAVING_USER_CUSTOMERS_ERROR';

export function saveUserCustomers(
	userId: number,
	customerIdsToAssign: number[],
	customerIdsToUnassign: number[],
	toastMessage: IToastMessage,
	filter: string,
	pageNumber: number
) {
	const body = JSON.stringify({ customerIdsToAssign, customerIdsToUnassign });

	return FetchAction.CreateFetchAction({
		url: endpoint.userChangeCustomerAssignment(userId),
		startAction: SAVING_USER_CUSTOMERS,
		body,
		method: 'POST',
		onAfterSuccess: (_, dispatch) => {
			toastr.success(toastMessage);
			dispatch(loadCustomers(userId, pageNumber, filter));
		},
	});
}

export const SAVING_NEW_USER = '@admin-users:SAVING_NEW_USER';
export const SAVING_NEW_USER_SUCCESS = '@admin-users:SAVING_NEW_USER_SUCCESS';
export const SAVING_NEW_USER_ERROR = '@admin-users:SAVING_NEW_USER_ERROR';

export function saveNewUser(user: INewUserRequest) {
	const body = JSON.stringify(user);

	return FetchActionGeneric.CreateFetchAction<IUser>({
		url: endpoint.users(),
		startAction: SAVING_NEW_USER,
		body,
		method: 'POST',
		onAfterSuccess: (payload: IUser, dispatch: DispatchFunc) => {
			dispatch(push(route.admin.editUser(payload.id)));
		},
	});
}

export const CHANGING_PASSWORD = '@admin-users:CHANGING_PASSWORD';
export const CHANGING_PASSWORD_SUCCESS =
	'@admin-users:CHANGING_PASSWORD_SUCCESS';
export const CHANGING_PASSWORD_ERROR = '@admin-users:CHANGING_PASSWORD_ERROR';

export function saveChangePassword(
	userId: number,
	password: string,
	toastMessage: IToastMessage
) {
	const body = JSON.stringify({ password });

	return FetchAction.CreateFetchAction({
		url: endpoint.changePassword(userId),
		startAction: CHANGING_PASSWORD,
		body,
		method: 'POST',
		onAfterSuccess: () => toastr.success(toastMessage),
	});
}

export const EDITING_USER = '@admin-users:EDITING_USER';
export const EDITING_USER_SUCCESS = '@admin-users:EDITING_USER_SUCCESS';
export const EDITING_USER_ERROR = '@admin-users:EDITING_USER_ERROR';

export function editUser(user: IUser) {
	const body = JSON.stringify(user);

	return FetchActionGeneric.CreateFetchAction<IUser>({
		url: endpoint.user(user.id),
		startAction: EDITING_USER,
		body,
		method: 'PATCH',
	});
}

export const DELETING_USER = '@admin-users:DELETING_USER';
export const DELETING_USER_SUCCESS = '@admin-users:DELETING_USER_SUCCESS';
export const DELETING_USER_ERROR = '@admin-users:DELETING_USER_ERROR';

export function deleteUser(userId: number, toastMessage: IToastMessage) {
	return FetchActionGeneric.CreateFetchAction<{}>({
		url: endpoint.user(userId),
		startAction: DELETING_USER,
		method: 'DELETE',
		onAfterSuccess: (payLoad, dispatch) => {
			toastr.success(toastMessage);
			dispatch(this.loadUsers());
		},
	});
}

export const RESET_USER_FILTERS = '@admin-users:RESET_USER_FILTERS';
export function resetUserFilters() {
	return {
		type: RESET_USER_FILTERS,
	};
}
